/*
  assets/mad-chat-clear.js  (QA2-05, 2026-09-26)

  The Shopify Inbox launcher (the "Chat" button, fixed in the bottom right corner, about 93 x 50 px) covered
  Buy door only, Price additional services and the glass tick on door pages, and Check out and Request your quote
  on the cart, from 375 to 1440 px. A fixed lift only moves the overlap onto another row, so this moves the launcher
  only while one of those controls is under it: just above them, and back to its corner once they have
  scrolled by. When clearing them would take it above 40% of the screen height, it steps aside (hidden) until
  they have passed. The chat app itself is untouched: nothing moves while the chat is open, and it keeps its own
  corner, size and settings.

  How: the launcher sits in <shopify-chat>, a fixed, full-screen host (pointer-events: none) with an open shadow
  root. The whole host is moved with the CSS translate property (assets/base.css, on every page, reads
  --mad-chat-lift; the class mad-chat-tucked hides it; neither applies while the host has the app's open
  attribute). Moving only the button (::part(activator)) would leave its wrapper, which takes clicks, over the
  control. Loaded by snippets/mad-quote-assets.liquid (door pages, cart, collections) and snippets/cart-drawer.liquid
  (every page while the cart is the slide-out tray, including the 404 page), and by the contact, Request a quote,
  project and trade page sections themselves (so they keep it if the cart type goes back to the notification); it
  does nothing on a page without the controls below.

  Codex final review R13 (2026-09-26): on the contact, Request a quote, project and trade pages at 375 and 390 px the
  launcher still sat on telephone links and on the field being filled in. Telephone and email links are now protected
  on every page, and so is the control that has focus (a text box, a choice, a button or a link), so the launcher moves
  off a field while it is being typed in and comes back when focus leaves. A focused control larger than half the
  screen is ignored (the page itself after the skip link), so the launcher does not simply hide.
  The same review found the launcher could drop back onto a button it had just cleared: the move is animated
  (translate, 0.15 s), and a second measurement during the animation read the launcher's half-moved position as its
  corner. Its corner is now worked out from the move actually shown at that moment (the computed translate).
  Codex re-check (2026-09-26): pressing the raised launcher takes focus from the field it was raised for, and the plan
  made at that focusout moved the launcher back to its corner before the pointer was released, so the press could miss
  it. While a pointer is down on the launcher nothing moves; the plan runs again once the pointer is released, after the
  click has reached the launcher (or after 3 seconds, if the release never arrives).
*/
(function () {
  'use strict';
  if (window.MadChatClear) return;

  var doc = document, root = doc.documentElement;
  /* The controls the launcher must never cover: the door page's buy block (the tick above Buy door only, Buy door
     only, Price additional services), the phone bottom bar, the cart's Check out, Request your quote and wallet
     buttons, the cart tray's tab or phone strip ([data-mad-tray-toggle], snippets/cart-drawer.liquid; the open
     tray hides the launcher itself, assets/component-cart-drawer.css), and any control a page marks with
     data-mad-chat-clear (the 404 page's search box and Search button, sections/mad-404-help.liquid; polish,
     2026-09-26: at 390 px the launcher sat on the right end of Search). */
  /* Door cards in grids stay unprotected (QA2-05): measured again 2026-10-08 at 390 px, protecting them made the
     launcher jump up and back 21 times in 39 scroll steps on All Doors. */
  var PROTECT = '.mad-pdp-glass-ack, [data-mad-buy-as-is], .mad-pdp-cta__button, [data-mad-sticky-cta], #checkout, [data-mad-finish-quote], #dynamic-checkout-wrapper, [data-mad-tray-toggle], [data-mad-chat-clear], a[href^="tel:"], a[href^="mailto:"]';
  /* R13: the control that has focus is protected too, while it has it: fields, choices, buttons and links. */
  var FOCUSED = 'input:not([type="hidden"]), select, textarea, button, a[href], summary, [contenteditable="true"]';
  var GAP = 8;           /* px kept between the launcher and a control */
  var HIGHEST = 0.4;     /* the launcher's top never goes above 40% of the screen height; it steps aside instead */
  var lift = 0, tucked = false, raf = 0, watched = null;

  /* Pure: where the launcher goes. base: its rect in its own corner (no lift); rects: the controls' rects;
     vh: the screen height. Returns { lift, tuck }: lift in px (0 = its corner), tuck = hide it for now. */
  function plan(base, rects, vh) {
    var off = 0;
    for (var i = 0; i < 12; i++) {
      var top = base.top - off - GAP, bottom = base.bottom - off + GAP, hit = null;
      for (var j = 0; j < rects.length; j++) {
        var r = rects[j];
        if (!(r.width > 0 && r.height > 0)) continue;
        if (r.right > base.left - GAP && r.left < base.right + GAP && r.bottom > top && r.top < bottom && (!hit || r.top < hit.top)) hit = r;
      }
      if (!hit) return { lift: off, tuck: false };
      off = base.bottom - hit.top + GAP;
      if (base.top - off < vh * HIGHEST) return { lift: 0, tuck: true };
    }
    return { lift: 0, tuck: true };
  }

  function host() { return doc.querySelector('shopify-chat'); }

  /* The lift the page shows right now: the host's computed translate (mid-animation it is between the old lift and
     the new one). Without getComputedStyle, the lift last set. */
  function shownLift(h) {
    if (!window.getComputedStyle) return lift;
    var t = String(window.getComputedStyle(h).translate || '');
    if (!t || t === 'none') return 0;
    var y = parseFloat(t.split(' ')[1] || '0');
    return isNaN(y) ? lift : -y;
  }

  /* The focused control's rect, when it is one to keep clear (not the launcher itself, not a whole page region). */
  function focusedRect(h, vh) {
    var el = doc.activeElement;
    if (!el || el === doc.body || el === root || el === h || !el.matches || !el.getBoundingClientRect) return null;
    if (!el.matches(FOCUSED)) return null;
    var r = el.getBoundingClientRect();
    if (vh && r.height > vh / 2) return null;
    return r;
  }
  /* Seth 2026-10-08 (launch review): Shopify's chat now puts its button one shadow root deeper, inside
     <shopify-agent-runtime>; look there too, so the bubble lifts off the glass tick and the buy buttons again. */
  function activator(h) {
    var sr = h && h.shadowRoot;
    if (!sr) return null;
    var a = sr.querySelector('[part~="activator"]');
    if (!a) {
      var rt = sr.querySelector('shopify-agent-runtime');
      a = rt && rt.shadowRoot ? rt.shadowRoot.querySelector('[part~="activator"]') : null;
    }
    return a;
  }

  function apply(l, t) {
    l = Math.max(0, Math.round(l));
    if (l !== lift) { lift = l; root.style.setProperty('--mad-chat-lift', l + 'px'); }
    if (t !== tucked) { tucked = t; root.classList.toggle('mad-chat-tucked', t); }
  }

  /* A pointer down on the launcher (the host, or anything in its shadow root): hold the plan until it is released. */
  var pressing = false, pressTimer = 0;
  function onLauncher(e) {
    var h = host(), t = e && e.target;
    if (!h || !t) return false;
    if (t === h || (h.contains && h.contains(t))) return true;
    var path = e.composedPath ? e.composedPath() : [];
    return !!path && path.indexOf(h) !== -1;
  }
  function press(e) {
    if (!onLauncher(e)) return;
    pressing = true;
    clearTimeout(pressTimer);
    pressTimer = setTimeout(release, 3000);
  }
  function release() {
    if (!pressing) return;
    pressing = false;
    clearTimeout(pressTimer);
    schedule(); /* the next frame, after the click this release makes has been delivered */
  }

  function update() {
    raf = 0;
    if (pressing) return;
    var h = host(), btn = activator(h);
    if (!btn) return;
    watch(h, btn);
    /* The chat is open: leave it where the app put it (the CSS also drops the lift on shopify-chat[open]). */
    if (h.hasAttribute('open') || btn.getAttribute('aria-expanded') === 'true') { apply(0, false); return; }
    var r = btn.getBoundingClientRect();
    if (!(r.width > 0 && r.height > 0)) return; /* hidden (a dialog is open) */
    var shown = shownLift(h);
    var base = { top: r.top + shown, bottom: r.bottom + shown, left: r.left, right: r.right };
    var vh = window.innerHeight || root.clientHeight || 0;
    var rects = Array.prototype.map.call(doc.querySelectorAll(PROTECT), function (el) { return el.getBoundingClientRect(); });
    var f = focusedRect(h, vh);
    if (f) rects.push(f);
    var p = plan(base, rects, vh);
    apply(p.lift, p.tuck);
  }

  function schedule() {
    if (raf) return;
    raf = window.requestAnimationFrame ? window.requestAnimationFrame(update) : setTimeout(update, 16);
  }

  /* Opening or closing the chat sets open on the host and aria-expanded on the launcher: settle at once. */
  function watch(h, btn) {
    if (watched === btn) return;
    watched = btn;
    if (window.MutationObserver) {
      new MutationObserver(schedule).observe(btn, { attributes: true, attributeFilter: ['aria-expanded'] });
      new MutationObserver(schedule).observe(h, { attributes: true, attributeFilter: ['open'] });
    }
    h.addEventListener('click', schedule);
  }

  if (window.addEventListener) {
    window.addEventListener('scroll', schedule, { passive: true });
    window.addEventListener('resize', schedule);
    window.addEventListener('load', schedule);
  }
  doc.addEventListener('mad:estimate:changed', schedule);
  doc.addEventListener('cart:updated', schedule);
  /* R13: a field that gains or loses focus (typing, Tab, a tap) is planned around at once. */
  doc.addEventListener('focusin', schedule);
  doc.addEventListener('focusout', schedule);
  /* Codex re-check: never while a pointer is down on the launcher (see the note at the top) */
  var DOWN = window.PointerEvent ? ['pointerdown'] : ['mousedown', 'touchstart'], UP = window.PointerEvent ? ['pointerup', 'pointercancel'] : ['mouseup', 'touchend', 'touchcancel'];
  DOWN.forEach(function (t) { doc.addEventListener(t, press, { capture: true, passive: true }); });
  if (window.addEventListener) UP.forEach(function (t) { window.addEventListener(t, release, { capture: true, passive: true }); });
  /* The page can change height without a scroll (the cart re-renders, a drawer opens). */
  function observeBody() { if (window.ResizeObserver && doc.body) new window.ResizeObserver(schedule).observe(doc.body); }
  if (doc.body) observeBody(); else doc.addEventListener('DOMContentLoaded', observeBody);
  /* The Inbox app adds its launcher some time after the page loads: look for it for up to 30 seconds. */
  var tries = 0;
  (function wait() { schedule(); if (!watched && ++tries < 60) setTimeout(wait, 500); })();

  window.MadChatClear = { plan: plan, update: update, PROTECT: PROTECT, FOCUSED: FOCUSED, pressing: function () { return pressing; } };
})();
